import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  onSnapshot,
  collection,
  query,
  orderBy,
  limit,
  getDocs,
} from 'firebase/firestore';
import { db } from '../firebase';

function todayKey(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const PASS_THRESHOLD = 70;

function formatDuration(seconds) {
  const s = Math.max(0, Math.round(Number(seconds) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

/** Record active or idle study seconds for today */
export async function recordStudyHeartbeat(userId, { activeSeconds = 0, idleSeconds = 0, subject = '' } = {}) {
  if (!userId) return;
  const day = todayKey();
  const ref = doc(db, 'studyTracking', userId);
  const snap = await getDoc(ref);
  const existing = snap.exists() ? snap.data() : {};
  const dailyLogs = { ...(existing.dailyLogs || {}) };
  const dayLog = dailyLogs[day] || { activeSeconds: 0, idleSeconds: 0, subjects: {}, loginCount: 0 };

  dayLog.activeSeconds = (dayLog.activeSeconds || 0) + activeSeconds;
  dayLog.idleSeconds = (dayLog.idleSeconds || 0) + idleSeconds;
  if (subject) {
    dayLog.subjects = { ...(dayLog.subjects || {}) };
    dayLog.subjects[subject] = (dayLog.subjects[subject] || 0) + activeSeconds;
  }
  dailyLogs[day] = dayLog;

  const totalActiveSeconds = (existing.totalActiveSeconds || 0) + activeSeconds;
  const totalIdleSeconds = (existing.totalIdleSeconds || 0) + idleSeconds;
  const loginDays = Object.keys(dailyLogs).length;

  await setDoc(
    ref,
    {
      dailyLogs,
      totalActiveSeconds,
      totalIdleSeconds,
      loginDays,
      lastActiveAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

/** Record portal open for today */
export async function recordPortalLogin(userId) {
  if (!userId) return;
  const day = todayKey();
  const ref = doc(db, 'studyTracking', userId);
  const snap = await getDoc(ref);
  const existing = snap.exists() ? snap.data() : {};
  const dailyLogs = { ...(existing.dailyLogs || {}) };
  const dayLog = dailyLogs[day] || { activeSeconds: 0, idleSeconds: 0, subjects: {}, loginCount: 0 };
  dayLog.loginCount = (dayLog.loginCount || 0) + 1;
  dailyLogs[day] = dayLog;

  await setDoc(
    ref,
    {
      dailyLogs,
      loginDays: Object.keys(dailyLogs).length,
      lastLoginAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

export function computeStudyStreak(dailyLogs = {}) {
  if (!dailyLogs || Object.keys(dailyLogs).length === 0) return 0;
  let streak = 0;
  const cursor = new Date();
  for (;;) {
    const key = todayKey(cursor);
    if (!dailyLogs[key]) break;
    streak += 1;
    cursor.setDate(cursor.getDate() - 1);
  }
  return streak;
}

export function weeklyStudyPercents(dailyLogs = {}, weeks = 6) {
  const result = [];
  const now = new Date();
  for (let w = weeks - 1; w >= 0; w -= 1) {
    let seconds = 0;
    for (let d = 0; d < 7; d += 1) {
      const date = new Date(now);
      date.setDate(now.getDate() - (w * 7 + d));
      const log = dailyLogs[todayKey(date)];
      seconds += Number(log?.activeSeconds) || 0;
    }
    const hours = seconds / 3600;
    result.push(Math.max(0, Math.min(100, Math.round((hours / 10) * 100))));
  }
  return result;
}

export function subscribeStudyTracking(userId, callback) {
  if (!userId) return () => {};
  return onSnapshot(doc(db, 'studyTracking', userId), (snap) => {
    callback(snap.exists() ? snap.data() : null);
  });
}

/** Admin: fetch all student tracking docs */
export async function fetchAllStudyTracking(limitCount = 100) {
  const snap = await getDocs(
    query(collection(db, 'studyTracking'), orderBy('lastActiveAt', 'desc'), limit(limitCount))
  );
  return snap.docs.map((d) => ({ userId: d.id, ...d.data() }));
}

/** AI-style weakness detection from progress data */
export function detectWeaknesses({ subjects = [], tracking = null, avgExamScore = null, quizzes = [] }) {
  const weaknesses = [];

  subjects.forEach((sub) => {
    if (sub.total > 0 && sub.pct < 40) {
      weaknesses.push({
        subject: sub.label || sub.id,
        reason: `Only ${sub.pct}% of ${sub.label} lectures completed`,
        severity: sub.pct < 20 ? 'high' : 'medium',
      });
    }
    if (sub.examPct != null && sub.examPct < PASS_THRESHOLD) {
      weaknesses.push({
        subject: sub.label || sub.id,
        reason: `Practice exam score ${sub.examPct}% (below ${PASS_THRESHOLD}% pass mark)`,
        severity: sub.examPct < 50 ? 'high' : 'medium',
      });
    }
  });

  const failedQuizzes = (quizzes || []).filter(
    (q) => (q.attempts || 0) > 0 && !q.passed && Number(q.lastPct) < PASS_THRESHOLD
  );
  failedQuizzes.slice(0, 4).forEach((q) => {
    weaknesses.push({
      subject: q.title || (q.kind === 'module' ? 'Module quiz' : 'Lecture quiz'),
      reason: `${q.kind === 'module' ? 'Module' : 'Lecture'} quiz score ${q.lastPct}% after ${q.attempts} ${q.attempts === 1 ? 'try' : 'tries'}`,
      severity: q.lastPct < 50 ? 'high' : 'medium',
    });
  });

  if (tracking?.dailyLogs) {
    const last7 = Object.entries(tracking.dailyLogs)
      .sort(([a], [b]) => b.localeCompare(a))
      .slice(0, 7);
    const avgDaily = last7.length
      ? last7.reduce((sum, [, log]) => sum + (log.activeSeconds || 0), 0) / last7.length
      : 0;
    if (avgDaily < 900) {
      weaknesses.push({
        subject: 'Study habits',
        reason: `Low daily study time (avg ${formatDuration(avgDaily)}/day this week)`,
        severity: 'medium',
      });
    }
    const idleRatio =
      last7.reduce((sum, [, log]) => sum + (log.idleSeconds || 0), 0) /
      Math.max(1, last7.reduce((sum, [, log]) => sum + (log.activeSeconds || 0) + (log.idleSeconds || 0), 0));
    if (idleRatio > 0.4) {
      weaknesses.push({
        subject: 'Focus',
        reason: 'High idle time while portal is open — focus on active studying',
        severity: 'medium',
      });
    }
  }

  if (avgExamScore != null && avgExamScore < PASS_THRESHOLD) {
    weaknesses.push({
      subject: 'Overall exams',
      reason: `Average exam score ${avgExamScore}% — more practice needed`,
      severity: 'high',
    });
  }

  return weaknesses.slice(0, 6);
}

export { todayKey, formatDuration, PASS_THRESHOLD };
